/*
 * Applies a freshly rendered `cart-drawer` section to the live drawer.
 *
 * Patches the two cart-dependent regions in place rather than replacing
 * `.drawer__inner`, so `form#CartDrawer-Form` survives the update. That matters for two
 * reasons, and they pull in the same direction:
 *
 *   1. The Supertime delivery-date block is injected above the checkout button, outside
 *      the form, and the app keys its "already injected here" check on the form element.
 *      Replace the form and the app re-injects; leave the form alone and it does not.
 *   2. The re-injected block comes back empty, so a shopper who picked a date and then
 *      changed the cart lost it. Keeping the block mounted keeps its React state, its
 *      hidden cart-attribute inputs, and the date the shopper chose.
 *
 * The invariant to preserve if this is ever rewritten: the form and the app's block are
 * replaced **together or not at all**. Replacing one but not the other is what produced
 * two date pickers (see STATUS.md, 2026-08-19).
 *
 * Falls back to replacing `.drawer__inner` whenever the empty state is on either side of
 * the swap, because that markup lives outside the two patched regions.
 *
 * Returns 'patched', 'replaced', or false when the drawer is not on the page.
 */
function patchCartDrawer(sourceRoot) {
  const REGIONS = ['#CartDrawer-CartItems', '.cart-drawer__footer'];

  const targetInner = document.querySelector('#CartDrawer .drawer__inner');
  const sourceInner = sourceRoot.querySelector('#CartDrawer .drawer__inner');
  if (!targetInner || !sourceInner) return false;

  const sourceDrawer = sourceRoot.querySelector('cart-drawer');
  const isEmpty = !!sourceDrawer && sourceDrawer.classList.contains('is-empty');

  const canPatch =
    !isEmpty &&
    !targetInner.querySelector('.drawer__inner-empty') &&
    REGIONS.every((selector) => targetInner.querySelector(selector) && sourceInner.querySelector(selector));

  if (canPatch) {
    REGIONS.forEach((selector) => {
      targetInner.querySelector(selector).innerHTML = sourceInner.querySelector(selector).innerHTML;
    });

    /* Outside both regions, so it would otherwise keep showing a resolved error. */
    const errors = targetInner.querySelector('#CartDrawer-CartErrors');
    if (errors) errors.textContent = '';
  } else {
    targetInner.innerHTML = sourceInner.innerHTML;
  }

  targetInner.classList.toggle('is-empty', isEmpty);
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) cartDrawer.classList.toggle('is-empty', isEmpty);
  const cartDrawerItems = targetInner.querySelector('cart-drawer-items');
  if (cartDrawerItems) cartDrawerItems.classList.toggle('is-empty', isEmpty);

  return canPatch ? 'patched' : 'replaced';
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    let drawerOutcome = false;

    this.getSectionsToRender().forEach((section) => {
      const html = parsedState.sections[section.id];
      if (!html) return;

      /* The drawer goes through patchCartDrawer so an in-flight delivery date survives
         the add to cart. Everything else is a plain section swap. */
      if (section.id === 'cart-drawer') {
        drawerOutcome = patchCartDrawer(new DOMParser().parseFromString(html, 'text/html'));
        return;
      }

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });

    setTimeout(() => {
      /* Only re-bind when the overlay node was actually replaced. Patching in place keeps
         the original node and its listener, so re-binding would close the drawer twice. */
      if (drawerOutcome !== 'patched') {
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  /* Quantity changes and line removals route the drawer through patchCartDrawer, so the
     form and the app's date block outlive the update. The selector above is only used to
     name the section in the request. */
  renderUpdatedSection(section, parsedState) {
    if (section.section !== 'cart-drawer') {
      super.renderUpdatedSection(section, parsedState);
      return;
    }

    const html = parsedState.sections[section.section];
    if (html) patchCartDrawer(new DOMParser().parseFromString(html, 'text/html'));
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
